import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CheckoutStateService } from 'src/app/shared-data/checkout-state.service';
import { Utils } from 'src/app/utils/utilities';
import { ZipYiElement } from '~services/biocatch/zip-yi-element.enum';
import { Denomination, DenominationOption, GiftCardV2 } from '~models';
import { SelectOptionV2Service } from 'src/app/shared-data';
import { Currency } from '~models/currency.model';

@Component({
  selector: 'app-select-option-v2',
  templateUrl: './select-option.component.html',
  styleUrls: ['./select-option.component.scss'],
  standalone: false,
})
export class SelectOptionV2Component implements OnInit, OnChanges {
  @Input() public giftCard: GiftCardV2;
  @Input() public denominations: Denomination[];
  @Input() public titleSelectOption: string;
  public customAmountValue: string;
  public minCustomPrice: number;
  public maxCustomPrice: number;
  public selectedOption: number | 'custom';
  public isFocused: boolean;
  public hideDenominations: boolean;
  public zipYiElement = ZipYiElement;

  constructor(
    public selectOptionV2Service: SelectOptionV2Service,
    public checkoutStateService: CheckoutStateService,
  ) {}

  ngOnInit() {
    this.resetState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.giftCard?.currentValue?.id !== changes?.giftCard?.previousValue?.id) {
      this.resetState();
    }
  }

  resetState() {
    this.selectedOption = null;
    this.hideDenominations = false;
    this.customAmountValue = '';
    this.isFocused = false;
    this.initDenominations();
  }

  initDenominations() {
    this.selectOptionV2Service.giftCard = { ...this.giftCard };
    this.selectOptionV2Service.denomonationOptions = [...this.denominations];

    if (this.selectOptionV2Service.giftCard?.customDenominations?.length > 0) {
      const DenominationWithCustom: DenominationOption = {
        price: 'custom',
        currency: Currency.AUD,
        stock: 'IN_STOCK',
      };

      if (this.selectOptionV2Service.denomonationOptions.length === 0) {
        this.selectedOption = 'custom';
        this.hideDenominations = true;
      }

      this.selectOptionV2Service.denomonationOptions.unshift(DenominationWithCustom);
      this.minCustomPrice = this.selectOptionV2Service.giftCard.customDenominations[0].minPrice;
      this.maxCustomPrice = this.selectOptionV2Service.giftCard.customDenominations[0].maxPrice;
    }

    this.setDefaultOptionSelected();
    this.updateIsSelected(this.selectedOption);
  }

  setDefaultOptionSelected() {
    const price = this.selectOptionV2Service.denomonationOptions.find((denominationOption) => {
      return (
        typeof denominationOption.price === 'number' &&
        denominationOption.price > 0 &&
        denominationOption.stock !== 'OUT_OF_STOCK'
      );
    })?.price;

    if (price) {
      this.selectedOption = price;
    }

    this.selectOptionV2Service.setSelectedCustomValue(this.selectedOption);
  }

  toggleDropdown(event: Event) {
    if (!this.checkoutStateService.isCheckoutBtnTriggered) {
      const dropdownWrapper = (event.target as HTMLInputElement).parentNode as HTMLElement;

      dropdownWrapper.classList.toggle('is-expanded');
    }
  }

  onSelect(event: Event, denominationOption: DenominationOption) {
    this.selectOptionV2Service.isValidData = true;
    this.selectOptionV2Service.isValidCustomAmount = true;
    this.selectOptionV2Service.isCustomAmount = false;
    this.customAmountValue = '';

    if (denominationOption.stock !== 'OUT_OF_STOCK') {
      const dropdownWrapper = (event.target as HTMLInputElement).closest('.select-option-wrapper');

      dropdownWrapper.classList.remove('is-expanded');
      this.selectedOption = denominationOption.price;
      this.selectOptionV2Service.setSelectedCustomValue(this.selectedOption);
      this.updateIsSelected(this.selectedOption);
    }
  }

  closeDropdown(event: Event) {
    const dropdownWrapper = (event.target as HTMLInputElement).parentNode as HTMLElement;

    dropdownWrapper.classList.remove('is-expanded');
  }

  changeCustomAmount(_event: Event) {
    this.selectOptionV2Service.isValidData = true;
    this.selectOptionV2Service.isValidCustomAmount = true;
    this.selectOptionV2Service.isCustomAmount = true;

    const customAmount = Number(this.customAmountValue);

    if (
      (customAmount < this.minCustomPrice && Utils.checkValid(this.customAmountValue)) ||
      (customAmount > this.maxCustomPrice && Utils.checkValid(this.customAmountValue))
    ) {
      this.selectOptionV2Service.isValidCustomAmount = false;
    }
  }

  handleFocusInputCustomAmount(_event: Event) {
    this.isFocused = true;
    this.selectOptionV2Service.isValidData = true;
    this.selectOptionV2Service.isValidCustomAmount = true;
    this.customAmountValue = this.customAmountValue.replace('$', '');
  }

  handleOutFocusInputCustomAmount(_event: Event) {
    this.isFocused = false;

    const customAmount = parseFloat(this.customAmountValue);
    if (Utils.checkValid(this.customAmountValue)) {
      const customAmountRounded = parseFloat(customAmount.toFixed(2));
      if (customAmountRounded < this.minCustomPrice || customAmountRounded > this.maxCustomPrice) {
        this.selectOptionV2Service.isValidCustomAmount = false;
      }

      this.customAmountValue = '$' + customAmountRounded.toString();
      this.selectOptionV2Service.isCustomAmount = true;
      this.selectOptionV2Service.setSelectedCustomValue(customAmountRounded);
    }

    this.selectOptionV2Service.setSelectedCustomValue(customAmount);
  }

  handleInputChangeCustomAmount(_event: Event) {
    const customAmount = parseFloat(this.customAmountValue);
    if (Utils.checkValid(this.customAmountValue)) {
      if (customAmount < this.minCustomPrice || customAmount > this.maxCustomPrice) {
        this.selectOptionV2Service.setSelectedCustomValue(null);
      } else {
        this.selectOptionV2Service.setSelectedCustomValue(customAmount);
      }
    } else {
      this.selectOptionV2Service.setSelectedCustomValue(null);
    }
  }

  updateIsSelected(price: number | 'custom') {
    this.selectOptionV2Service.denomonationOptions.forEach((option) => {
      if (option.price === price) {
        option.isSelected = true;
      } else {
        option.isSelected = false;
      }
    });
  }
}
